import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { BRAND_TAGLINE } from "../lib/brand";
import {
  Facebook, Instagram, Youtube, Linkedin, Twitter, MessageCircle, Send,
  Mail, Phone, MapPin, ShieldCheck, ArrowUpRight,
} from "lucide-react";

/**
 * v2.64 — Professional editable footer.
 *
 * All copy/links/socials come from `/api/config` → `content.footer` (admin-editable
 * via /admin?tab=content → "Footer"). Defaults render if admin hasn't customized.
 */
const SOCIAL_META = {
  facebook: { Icon: Facebook, label: "Facebook", hover: "hover:bg-[#1877F2]" },
  instagram: { Icon: Instagram, label: "Instagram", hover: "hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]" },
  youtube: { Icon: Youtube, label: "YouTube", hover: "hover:bg-[#FF0000]" },
  linkedin: { Icon: Linkedin, label: "LinkedIn", hover: "hover:bg-[#0A66C2]" },
  twitter: { Icon: Twitter, label: "X (Twitter)", hover: "hover:bg-black" },
  whatsapp: { Icon: MessageCircle, label: "WhatsApp", hover: "hover:bg-[#25D366]" },
  telegram: { Icon: Send, label: "Telegram", hover: "hover:bg-[#26A5E4]" },
};

const normalizeSocialHref = (key, raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  // Helpers for common bare-handle inputs
  if (key === "whatsapp") {
    const digits = v.replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }
  if (key === "telegram") {
    const handle = v.replace(/^@/, "");
    return `https://t.me/${handle}`;
  }
  if (key === "twitter") {
    const handle = v.replace(/^@/, "");
    return `https://x.com/${handle}`;
  }
  if (key === "instagram") {
    const handle = v.replace(/^@/, "");
    return `https://instagram.com/${handle}`;
  }
  if (key === "youtube") return `https://youtube.com/@${v.replace(/^@/, "")}`;
  if (key === "linkedin") return v.includes("/") ? `https://${v}` : `https://linkedin.com/company/${v}`;
  if (key === "facebook") return v.includes("/") ? `https://${v}` : `https://facebook.com/${v}`;
  return v;
};

export default function Footer() {
  const [pages, setPages] = useState([]);
  const { pathname } = useLocation();
  const { isTeamMember, loading } = useAuth();
  const { content } = useConfig();
  const f = content?.footer || {};

  useEffect(() => {
    api.get("/pages").then((r) => setPages(r.data || [])).catch(() => {});
  }, []);

  // v2.65 — Symmetric hide list with Header.jsx so auth pages, team console
  // and plans-preview have consistent "minimal layout" (no header AND no footer).
  // Path-based hide is synchronous (no flicker). Team-member unmount is deferred
  // until auth `loading` resolves to prevent the brief render-then-vanish on refresh.
  const AUTH_ROUTES = ["/team", "/team-login", "/access", "/login", "/register", "/forgot-password", "/magic", "/reset-password", "/plans-preview", "/office"];
  const onAuthRoute = AUTH_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
  if (onAuthRoute || (!loading && isTeamMember)) return null;

  const year = new Date().getFullYear();
  const copy = (f.copyright || "© {year} HouseEntry Solutions. All rights reserved.").replace("{year}", year);
  const quickLinks = (f.quick_links && f.quick_links.length) ? f.quick_links : [
    { label: "Listings", href: "/" },
    { label: "Projects", href: "/projects" },
    { label: "Map View", href: "/map" },
    { label: "Pricing", href: "/pricing" },
  ];
  const socials = Object.entries(SOCIAL_META)
    .map(([key, meta]) => ({ key, meta, href: normalizeSocialHref(key, (f.social || {})[key]) }))
    .filter((s) => s.href);

  return (
    <footer className="mt-12 md:mt-20 bg-[#09090B] text-[#E4E4E7] border-t border-[#27272A] relative" data-testid="site-footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#0055FF]/40 to-transparent" />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-8 md:py-14">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Brand + about + socials */}
          <div className="lg:col-span-5">
            <div className="font-heading font-bold text-2xl tracking-tight text-white mb-1">
              House<span className="text-[#E53935]">Entry</span> <span className="text-[#A1A1AA] font-medium">Solutions</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] font-semibold mb-4">{BRAND_TAGLINE}</div>
            <p className="text-[13px] text-[#A1A1AA] max-w-md leading-relaxed mb-5" data-testid="footer-about">
              {f.about_text || "A centralized property intelligence platform — verified listings, projects, and exclusive deals in West Delhi. Built for serious clients."}
            </p>

            {/* Social icons row */}
            {socials.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="footer-socials">
                {socials.map(({ key, meta, href }) => {
                  const { Icon, label, hover } = meta;
                  return (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                      className={`w-9 h-9 rounded-full bg-[#18181B] border border-[#27272A] text-[#A1A1AA] flex items-center justify-center transition-all duration-200 ${hover} hover:text-white hover:border-transparent hover:scale-110`}
                      data-testid={`footer-social-${key}`}
                    >
                      <Icon size={15} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#71717A] mb-3">Platform</div>
            <ul className="space-y-2 text-[13px]" data-testid="footer-quick-links">
              {quickLinks.map((q) => {
                const isExternal = /^https?:\/\//i.test(q.href);
                return (
                  <li key={`${q.label}-${q.href}`}>
                    {isExternal ? (
                      <a href={q.href} target="_blank" rel="noopener noreferrer" className="text-[#D4D4D8] hover:text-white transition-colors inline-flex items-center gap-1">
                        {q.label} <ArrowUpRight size={11} className="opacity-60" />
                      </a>
                    ) : (
                      <Link to={q.href} className="text-[#D4D4D8] hover:text-white transition-colors">
                        {q.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Company / CMS pages */}
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#71717A] mb-3">Company</div>
            <ul className="space-y-2 text-[13px]">
              {pages.map((p) => (
                <li key={p.slug}>
                  <Link to={`/page/${p.slug}`} className="text-[#D4D4D8] hover:text-white transition-colors" data-testid={`footer-page-${p.slug}`}>
                    {p.title || p.slug}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#71717A] mb-3">Contact</div>
            <ul className="space-y-2.5 text-[13px] text-[#D4D4D8]" data-testid="footer-contact">
              {f.address && (
                <li className="flex items-start gap-2">
                  <MapPin size={13} className="text-[#71717A] mt-0.5 shrink-0" />
                  <span className="text-[12.5px] leading-relaxed">{f.address}</span>
                </li>
              )}
              {f.phone && (
                <li className="flex items-center gap-2">
                  <Phone size={13} className="text-[#71717A] shrink-0" />
                  <a href={`tel:${f.phone.replace(/\s+/g, "")}`} className="hover:text-white transition-colors">{f.phone}</a>
                </li>
              )}
              {f.email && (
                <li className="flex items-center gap-2">
                  <Mail size={13} className="text-[#71717A] shrink-0" />
                  <a href={`mailto:${f.email}`} className="hover:text-white transition-colors break-all">{f.email}</a>
                </li>
              )}
              {f.rera_id && (
                <li className="flex items-center gap-2 pt-2 mt-1 border-t border-[#27272A]">
                  <ShieldCheck size={13} className="text-[#15803D] shrink-0" />
                  <span className="text-[11.5px] text-[#A1A1AA]">RERA: <span className="text-white font-mono-data">{f.rera_id}</span></span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[#27272A] flex flex-wrap items-center justify-between gap-3 text-[11px] text-[#71717A]">
          <div data-testid="footer-copyright">{copy}</div>
          <div className="flex items-center gap-3">
            <Link to="/page/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <span className="text-[#3F3F46]">·</span>
            <Link to="/page/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
